import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/auth.service';
import { apiService } from '../services/api.service';
import { useTenant } from './TenantContext';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const { tenantInfo, isLoading: isTenantLoading } = useTenant();
  const navigate = useNavigate();

  // Initialize auth state
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Wait for tenant context to be initialized
        if (isTenantLoading) {
          console.log('AuthContext: Waiting for tenant context to initialize...');
          return;
        }

        console.log('AuthContext: Starting session verification');
        const sessionResponse = await authService.verifySession();
        console.log('AuthContext: Session verification result:', sessionResponse);
        
        if (sessionResponse && sessionResponse.valid && sessionResponse.user) {
          console.log('AuthContext: Setting user from session:', sessionResponse.user);
          setUser(sessionResponse.user);
          // Fetch additional user details from tenant-users API
          await fetchUserDetails(sessionResponse.user.user_id);
        } else {
          console.log('AuthContext: No valid session found');
        }
      } catch (error) {
        console.error('AuthContext: Failed to verify session:', error);
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, [isTenantLoading]); // Add isTenantLoading as a dependency

  // Function to fetch additional user details
  const fetchUserDetails = async (userId) => {
    try {
      if (!userId) return;
      
      const userDetails = await apiService.get(`/tenant-users/users/${userId}`);
      if (userDetails) {
        // Merge the additional details with the existing user object
        setUser(prevUser => ({
          ...prevUser,
          ...userDetails,
          // Ensure we don't overwrite critical auth properties
          user_id: prevUser.user_id,
          username: prevUser.username,
          email: prevUser.email
        }));
      }
    } catch (error) {
      console.error('Failed to fetch user details:', error);
      // Don't throw the error - just log it and continue with the existing user data
      // This prevents the 404 from breaking the application
    }
  };

  const handleGoogleLogin = async (response) => {
    try {
      const result = await authService.handleGoogleLogin(response);
      if (result && result.user) {
        setUser(result.user);
        // Fetch additional user details after login
        await fetchUserDetails(result.user.user_id);
        navigate('/');
        return true;
      }
      return false;
    } catch (error) {
      console.error('Google login failed:', error);
      return false;
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
      setUser(null);
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const isAuthenticated = () => {
    return !!user;
  };

  const value = {
    user,
    loading,
    handleGoogleLogin,
    logout,
    isAuthenticated,
    authConfig: tenantInfo?.auth || null,
    refreshUserDetails: () => user ? fetchUserDetails(user.user_id) : Promise.resolve()
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
